import styled, {css} from "styled-components";
import {brand, fontFamilies, radii} from "theme/tokens";
import type {SponsorBookingState} from "./SponsorApi";
import {ConsentText} from "pay/ImmediateDeliveryConsent.styles";

/**
 * Externalized styles for {@link ./SponsorMyBookings} (epic #441). Every colour is a `theme.tokens`
 * read, so the advertiser's own list follows light and dark with no second stylesheet.
 *
 * Deliberately NOT imported from `account/admin/AdminPage.styles.ts`, whose state chip looks the same:
 * `/sponsor` is a public, eagerly pre-rendered route, and importing the operator page's stylesheet would
 * drag the whole admin chunk into the public bundle to save a dozen declarations. The token VOCABULARY
 * is shared instead of the module — same fills, same inks, no coupling.
 */

export const List = styled.ul`
	margin: 0;
	padding: 0;
	list-style: none;
	display: flex;
	flex-direction: column;
	gap: 12px;
`;

/**
 * One booking. `$highlighted` is the row a `?booking=` link named. The approval email lands on this
 * page and the advertiser has to find the booking it is about, which in a long list is not obvious.
 *
 * Drawn as a ring plus a lifted surface rather than as a colour wash: the row already carries a state
 * chip whose colour MEANS something, and tinting the row behind it would put two colour signals on one
 * booking saying different things.
 */
export const Row = styled.li<{$highlighted?: boolean}>`
	display: flex;
	gap: 14px;
	align-items: flex-start;
	padding: 14px;
	border: 1px solid ${({theme}) => theme.tokens.line};
	border-radius: ${radii.panel};
	background: ${({theme}) => theme.tokens.card};
	scroll-margin-block: 96px;

	${({$highlighted}) => $highlighted && css`
		border-color: ${({theme}) => theme.tokens.blue};
		box-shadow: 0 0 0 3px ${({theme}) => theme.tokens.accentTint};
	`};

	@media (max-width: 520px) {
		flex-direction: column;
	}
`;

/** The advertiser's own first creative, as the row's visual handle. */
export const Thumbnail = styled.img`
	flex: none;
	width: 96px;
	aspect-ratio: 16 / 10;
	object-fit: cover;
	border-radius: ${radii.chip};
	border: 1px solid ${({theme}) => theme.tokens.line};
	background: ${({theme}) => theme.tokens.field};
`;

export const RowBody = styled.div`
	flex: 1 1 auto;
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

export const RowHead = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 10px;
	flex-wrap: wrap;
`;

export const Headline = styled.h3`
	margin: 0;
	font-size: 15px;
	font-weight: 700;
	line-height: 1.3;
	color: ${({theme}) => theme.tokens.ink};
	/* The advertiser chose this text and it is bounded server-side, but a long one must not push the
	   state chip off the row. */
	overflow-wrap: anywhere;
`;

/* Every server-side booking state, each with a distinct fill AND a distinct ink, never hue alone. */
const stateChipCss = {
	PENDING_REVIEW: css`
		background: ${({theme}) => theme.tokens.goldTint};
		color: ${({theme}) => theme.tokens.orange};
	`,
	/*
	 * Approved and unpaid (kitecorp/transferify-api-service#1362). It reads as an OPEN item rather than
	 * a settled one. The same gold family as "awaiting review", not the grey the abandoned
	 * `PENDING_PAYMENT` wore, because this booking is holding a brand's calendar and somebody still
	 * has to act on it.
	 */
	AWAITING_PAYMENT: css`
		background: ${({theme}) => theme.tokens.goldTint};
		color: ${({theme}) => theme.tokens.orange};
		/* The one distinguishing mark against AWAITING REVIEW above, since the two share a fill: never
		   hue alone, and never the fill alone either. */
		box-shadow: inset 0 0 0 1px ${({theme}) => theme.tokens.orange};
	`,
	APPROVED: css`
		background: ${({theme}) => theme.tokens.accentTint};
		color: ${({theme}) => theme.tokens.blue};
	`,
	ACTIVE: css`
		background: ${({theme}) => theme.tokens.signalTint};
		color: ${({theme}) => theme.tokens.signalInk};
	`,
	ENDED: css`
		background: ${({theme}) => theme.tokens.chip};
		color: ${({theme}) => theme.tokens.ink2};
	`,
	REJECTED: css`
		background: ${({theme}) => theme.tokens.field};
		color: ${({theme}) => theme.tokens.errorInk};
	`,
	/* Terminal and nobody's fault: the dates went back on the calendar. Settled ink, not error ink. */
	WITHDRAWN: css`
		background: ${({theme}) => theme.tokens.chip};
		color: ${({theme}) => theme.tokens.ink3};
	`,
	EXPIRED: css`
		background: ${({theme}) => theme.tokens.chip};
		color: ${({theme}) => theme.tokens.ink3};
	`,
	// `satisfies Record<SponsorBookingState, …>` is the point: a seventh server state becomes a compile
	// error here instead of an unstyled chip nobody notices.
} as const satisfies Record<SponsorBookingState, ReturnType<typeof css>>;

export const StateChip = styled.span<{$state: SponsorBookingState}>`
	flex: none;
	padding: 4px 10px;
	border-radius: ${radii.pill};
	font-family: ${fontFamilies.mono};
	font-size: 10.5px;
	font-weight: 700;
	letter-spacing: 0.05em;
	text-transform: uppercase;
	${({$state}) => stateChipCss[$state]};
`;

/** Right-hand cluster of the row head: the run count beside the state chip, as the operator queue has it. */
export const RowStatus = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	flex-wrap: wrap;
`;

/** Self-describing ("412 impressions"), so it carries no separate term and lives outside the `<dl>`. */
export const Impressions = styled.span`
	font-size: 12.5px;
	font-variant-numeric: tabular-nums;
	color: ${({theme}) => theme.tokens.ink2};
`;

/** Dates, length and price — the facts of the booking, on one wrapping line. */
export const Facts = styled.dl`
	margin: 0;
	display: flex;
	flex-wrap: wrap;
	gap: 4px 18px;
	font-size: 12.5px;
	color: ${({theme}) => theme.tokens.ink2};
`;

export const Fact = styled.div`
	display: flex;
	align-items: baseline;
	gap: 6px;
`;

export const FactTerm = styled.dt`
	margin: 0;
`;

export const FactValue = styled.dd`
	margin: 0;
	font-weight: 700;
	font-variant-numeric: tabular-nums;
	color: ${({theme}) => theme.tokens.ink};
`;

/**
 * The operator's rejection reason. Given its own bordered block rather than a grey caption: it is the
 * only explanation the advertiser gets for a refused campaign and a refund, so it must not read as fine
 * print. `white-space: pre-line` keeps the reviewer's own line breaks, since the text is shown verbatim.
 */
export const Rejection = styled.div`
	padding: 10px 12px;
	border-radius: ${radii.control};
	background: ${({theme}) => theme.tokens.field};
	border: 1px solid ${brand.error};
	color: ${({theme}) => theme.tokens.ink};
	font-size: 13px;
	line-height: 1.5;
	white-space: pre-line;
	overflow-wrap: anywhere;
`;

export const RejectionLabel = styled.span`
	display: block;
	margin-bottom: 4px;
	font-size: 11.5px;
	font-weight: 700;
	letter-spacing: 0.04em;
	text-transform: uppercase;
	color: ${({theme}) => theme.tokens.errorInk};
`;

/** A quieter note for a booking whose checkout was simply abandoned — a fact, not a failure. */
export const RowNote = styled.p`
	margin: 0;
	font-size: 12.5px;
	line-height: 1.5;
	color: ${({theme}) => theme.tokens.ink2};
`;

/**
 * The payment call to action on an approved, unpaid booking
 * (kitecorp/transferify-api-service#1362).
 *
 * The deadline sits BESIDE the button rather than inside it: it is a date somebody may want to write
 * down or check against a calendar, and a button label is not where a reader looks for one.
 */
export const PayRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	flex-wrap: wrap;
	padding-top: 4px;
`;

export const PayNote = styled.p`
	margin: 0;
	flex: 1 1 220px;
	font-size: 12.5px;
	line-height: 1.5;
	color: ${({theme}) => theme.tokens.ink};
`;

/**
 * `type="button"` explicitly. This list renders on the same page as the booking form, and a bare
 * `<button>` inside one would submit it; that the two happen not to be nested today is not a property
 * worth relying on.
 */
export const PayButton = styled.button.attrs({type: "button"})`
	flex: none;
	height: 40px;
	padding: 0 20px;
	border: none;
	border-radius: ${radii.control};
	font-family: ${fontFamilies.sans};
	font-size: 14px;
	font-weight: 700;
	cursor: pointer;
	transition: 0.16s;
	background: ${({theme}) => theme.tokens.blue};
	color: ${({theme}) => theme.tokens.blueInk};

	&:hover:not(:disabled) {
		background: ${({theme}) => theme.tokens.blued};
	}

	&:disabled {
		cursor: default;
		background: ${({theme}) => theme.tokens.field};
		color: ${({theme}) => theme.tokens.ink3};
	}

	&:focus-visible {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
	}
`;

/**
 * The sentence under the pay row that says what "Pay now" concludes, with the Advertising Terms link
 * inside it (kitecorp/transferify-ui#2602). The checkout consent's own text ramp, as a paragraph: two
 * sentences about the same document should not be able to drift apart in size.
 */
export const PayTerms = styled(ConsentText).attrs({as: "p"})`
	margin: 6px 0 0;
`;
